import { getCornerPositions } from '@/utils/image-layer-crop';
import { rotatePoint, translateYReverse } from '@/utils/math-kit';
import { ILayerProps, IPoint } from '@/version-types';

export type AnchorCorner = 'leftTop' | 'rightTop' | 'leftBottom' | 'rightBottom';
export type AnchorEdge = 'top' | 'bottom' | 'left' | 'right';

export type AnchorSpec = { kind: 'corner'; corner: AnchorCorner } | { kind: 'edge'; edge: AnchorEdge };

/** 图层局部角点/边点（中心原点、y 向上）→ 画布上相对中心的偏移 */
export const localToCenterOffset = (local: IPoint, rotate: number): IPoint =>
    rotatePoint(translateYReverse(local), -rotate);

export const getLayerCenter = (layer: ILayerProps): IPoint => ({
    x: layer.left + layer.width / 2,
    y: layer.top + layer.height / 2,
});

/** 锚点在画布坐标系下的位置 */
export const getAnchorWorld = (layer: ILayerProps, anchorLocal: IPoint): IPoint => {
    const center = getLayerCenter(layer);
    const offset = localToCenterOffset(anchorLocal, layer.rotate);
    return { x: center.x + offset.x, y: center.y + offset.y };
};

export const getAnchorLocalPoint = (width: number, height: number, spec: AnchorSpec): IPoint => {
    if (spec.kind === 'corner') {
        return getCornerPositions(width, height)[spec.corner];
    }

    switch (spec.edge) {
        case 'top':
            return { x: 0, y: height / 2 };
        case 'bottom':
            return { x: 0, y: -height / 2 };
        case 'left':
            return { x: -width / 2, y: 0 };
        case 'right':
            return { x: width / 2, y: 0 };
    }
};

/** 固定锚点世界坐标，反推新 left/top */
export const computeLeftTopFromPinnedAnchor = (
    anchorWorld: IPoint,
    width: number,
    height: number,
    rotate: number,
    anchorLocal: IPoint,
): { left: number; top: number } => {
    const offset = localToCenterOffset(anchorLocal, rotate);
    const center = { x: anchorWorld.x - offset.x, y: anchorWorld.y - offset.y };
    return { left: center.x - width / 2, top: center.y - height / 2 };
};

/** 由本帧实际的 left/top 变化反推 movePoint，保证与锚点位置一致 */
export const getMovePointFromPositionDelta = (
    base: ILayerProps,
    newLeft: number,
    newTop: number,
    newWidth: number,
    newHeight: number,
): IPoint => {
    const moveDistance = {
        x: newLeft + newWidth / 2 - (base.left + base.width / 2),
        y: newTop + newHeight / 2 - (base.top + base.height / 2),
    };
    return translateYReverse(rotatePoint(translateYReverse(moveDistance), base.rotate));
};

export const resolveAnchorSpec = (operateType: string): AnchorSpec | null => {
    if (operateType.includes('crop-layer-scale-lt')) {
        return { kind: 'corner', corner: 'rightBottom' };
    }
    if (operateType.includes('crop-layer-scale-rt')) {
        return { kind: 'corner', corner: 'leftBottom' };
    }
    if (operateType.includes('crop-layer-scale-lb')) {
        return { kind: 'corner', corner: 'rightTop' };
    }
    if (operateType.includes('crop-layer-scale-rb')) {
        return { kind: 'corner', corner: 'leftTop' };
    }
    if (operateType.includes('crop-layer-resize-tc')) {
        return { kind: 'edge', edge: 'bottom' };
    }
    if (operateType.includes('crop-layer-resize-bc')) {
        return { kind: 'edge', edge: 'top' };
    }
    if (operateType.includes('crop-layer-resize-lc')) {
        return { kind: 'edge', edge: 'right' };
    }
    if (operateType.includes('crop-layer-resize-rc')) {
        return { kind: 'edge', edge: 'left' };
    }
    return null;
};
